import { useEffect, useRef, type RefObject } from "react";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

let activeDialogCount = 0;
let previousBodyOverflow = "";

export function lockBodyScroll() {
  if (typeof document === "undefined") return;
  if (activeDialogCount === 0) {
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  activeDialogCount++;
}

export function unlockBodyScroll() {
  if (typeof document === "undefined") return;
  activeDialogCount = Math.max(0, activeDialogCount - 1);
  if (activeDialogCount === 0) {
    document.body.style.overflow = previousBodyOverflow;
  }
}

export function getActiveDialogCount(): number {
  return activeDialogCount;
}

export function getFocusableElements(container: HTMLElement | null): HTMLElement[] {
  if (!container) return [];
  const elements = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  return elements.filter((el) => {
    if (el.hasAttribute("disabled") || el.getAttribute("aria-disabled") === "true") return false;
    if (el.hasAttribute("hidden") || el.getAttribute("aria-hidden") === "true") return false;
    if (typeof window !== "undefined") {
      const style = window.getComputedStyle(el);
      if (style.display === "none" || style.visibility === "hidden") return false;
    }
    return true;
  });
}

export type UseDialogAccessibilityOptions = {
  open: boolean;
  containerRef: RefObject<HTMLDivElement | null>;
  onClose: () => void;
  disableEscape?: boolean;
  busy?: boolean;
};

export function useDialogAccessibility({
  open,
  containerRef,
  onClose,
  disableEscape = false,
  busy = false,
}: UseDialogAccessibilityOptions) {
  const triggerElementRef = useRef<HTMLElement | null>(null);
  const optionsRef = useRef({ onClose, disableEscape, busy });

  useEffect(() => {
    optionsRef.current = { onClose, disableEscape, busy };
  });

  useEffect(() => {
    if (!open) return;

    // 1. Lock body scroll (ref-counted across nested dialogs)
    lockBodyScroll();

    // 2. Remember trigger element for focus restoration
    if (typeof document !== "undefined") {
      triggerElementRef.current = document.activeElement as HTMLElement;
    }

    // 3. Set initial focus within dialog
    const container = containerRef.current;
    if (container) {
      const focusables = getFocusableElements(container);
      if (focusables.length > 0) {
        // Focus first interactive element (close button or first control)
        focusables[0].focus();
      } else {
        // If no focusable children exist, ensure container is focusable and focus it
        if (!container.hasAttribute("tabindex")) {
          container.setAttribute("tabindex", "-1");
        }
        container.focus();
      }
    }

    // 4. Keyboard trap (Tab / Shift+Tab) and Escape handler
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      const { onClose: currentOnClose, disableEscape: currentDisableEscape, busy: currentBusy } = optionsRef.current;
      if (e.key === "Escape" && !currentDisableEscape && !currentBusy) {
        e.preventDefault();
        currentOnClose();
        return;
      }

      if (e.key === "Tab") {
        const currentContainer = containerRef.current;
        if (!currentContainer) return;

        const focusables = getFocusableElements(currentContainer);
        if (focusables.length === 0) {
          e.preventDefault();
          currentContainer.focus();
          return;
        }

        const firstElement = focusables[0];
        const lastElement = focusables[focusables.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement || document.activeElement === currentContainer) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    // 5. Cleanup: unlock body scroll & restore focus to trigger element
    return () => {
      unlockBodyScroll();
      document.removeEventListener("keydown", handleKeyDown);
      if (triggerElementRef.current && typeof triggerElementRef.current.focus === "function") {
        triggerElementRef.current.focus();
      }
    };
  }, [open, containerRef]);
}
